import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useStorage } from '~Components/Storage';
import { useBridgeApi, useIntegrationCheck, useLicenses, useLogger, useSelector } from '~Hooks';
import { GetActiveOrders, GetActiveOrdersVariables } from './__queries__';
import { useIncomingOrderUtility } from '~Scenes/EventedKitchenDisplay/Hooks/useIncomingOrdersUtility';
import { ACCEPT_ORDER, FULL_ORDER, REJECT_ORDER } from '~Queries/orders';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import {
  AcceptPendingOrder,
  AcceptPendingOrderVariables,
  GetFullOrder,
  GetPendingOrders_venue_orders,
  RejectPendingOrder,
  RejectPendingOrderVariables,
} from '~Queries/__queries__';
import { DateTime } from 'luxon';

const ACTIVE_ORDERS_QUERY = gql`
  query GetActiveOrders($venueId: ID!) {
    orders: allOrders(filter: { venue: { id: $venueId }, status: PENDING }) {
      id
      easyId
      status
      createdAt
    }
  }
`;

const GET_VENUE_PREP_TIME = gql`
  query GetVenuePrepTime($venueId: ID!) {
    Venue(id: $venueId) {
      id
      minimumPickupWaitTime
    }
  }
`;

const determineEstimatedTime = (
  order: GetPendingOrders_venue_orders,
  prepTimeMinutes: number,
): string => {
  // The closest time the order can be delivered, fallback 15 min
  const asapTime = DateTime.now().plus({ minutes: prepTimeMinutes || 15 });

  // If the customer has a desired time, check if we can use it
  if (order.desiredTime) {
    const desiredTime = DateTime.fromISO(order.desiredTime);

    if (!desiredTime.isValid) {
      return '';
    }
    // Check that the desired time isn't before the asap time
    if (desiredTime > asapTime) {
      return desiredTime.toISO();
    }
  }

  // If the user selects a different time with the x/- buttons we want to use it
  if (order.estimatedFinishTime && DateTime.fromISO(order.estimatedFinishTime) > asapTime) {
    return order.estimatedFinishTime;
  }

  // If the desired time is before the asap time, or there is no desired time
  return asapTime.toISO();
};

interface ContextType {
  pendingOrdersIds: string[];
  ordersToHandle: GetPendingOrders_venue_orders[];
  orderFetchingLoading: boolean;
  isAutoAccepting: boolean;
  acceptOrder: (order: GetPendingOrders_venue_orders) => Promise<void>;
  rejectOrder: (orderId: string, reason: string) => Promise<void>;
  getOrderById: (orderId: string) => Promise<GetPendingOrders_venue_orders | null>;
  showingIncomingModal: boolean;
  showModal: () => void;
  hideModal: () => void;
  prepTime: {
    time: number;
    loading: boolean;
    refetch: () => void;
  };
}

interface ProviderProps {
  children: React.ReactNode;
}

const Context = createContext<ContextType>({} as ContextType);

const MAX_RETRIES = 10;

export const useIncomingAppOrders = () => useContext(Context);

export default function IncomingAppOrdersProvider({ children }: ProviderProps) {
  const logger = useLogger('incoming-app-orders');
  const licenses = useLicenses();
  const bridgeApi = useBridgeApi();
  const [storage] = useStorage();
  const storageRef = useRef(storage);
  const venueId = useSelector((state) => state.venueId);
  const [pendingOrderIds, setPendingOrderIds] = useState<string[]>([]);
  const [isAutoAccepting, setIsAutoAccepting] = useState(false);
  const [showingIncomingModal, setShowingIncomingModal] = useState(false);
  const [prepTime, setPrepTime] = useState(15); // Default to 15 minutes
  const acceptedOrderIdsRef = useRef<Set<string>>(new Set());
  const retryCountsRef = useRef<Map<string, number>>(new Map());

  // Pathname
  const pageHref = window?.location.href;

  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  // Used for manual accept
  const [orderFetchingLoading, setOrderFetchingLoading] = useState(false);
  const [ordersToHandle, setOrdersToHandle] = useState<GetPendingOrders_venue_orders[]>([]);

  const ordersQuery = useQuery<GetActiveOrders, GetActiveOrdersVariables>(ACTIVE_ORDERS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: { venueId: venueId || '' },
    pollInterval: 4000,
    skip: !venueId || !licenses.hasApp,
  });

  // Refetch the prep time every 15 minutes
  const prepTimeQuery = useQuery(GET_VENUE_PREP_TIME, {
    variables: { venueId },
    skip: !venueId,
    fetchPolicy: 'no-cache',
    pollInterval: 60 * 1000 * 15,
  });

  useEffect(() => {
    if (!prepTimeQuery.data) {
      return;
    }

    setPrepTime(prepTimeQuery.data?.Venue?.minimumPickupWaitTime || 15);
  }, [prepTimeQuery.data]);

  const client = useApolloClient();

  const hasSKVIntegration = useIntegrationCheck('SKATTEVERKET');
  const hasSKVDevice = storage.taxComplianceDeviceId;

  const orderUtility = useIncomingOrderUtility();

  const [acceptOrderMutation] = useMutation<AcceptPendingOrder, AcceptPendingOrderVariables>(
    ACCEPT_ORDER,
  );

  const [reject] = useMutation<RejectPendingOrder, RejectPendingOrderVariables>(REJECT_ORDER);

  // Fetch the full order from the server when accept is triggered
  const getOrderById = async (orderId: string): Promise<GetPendingOrders_venue_orders | null> => {
    const getFullOrderQuery = await client.query<GetFullOrder>({
      query: FULL_ORDER,
      variables: { orderId },
      fetchPolicy: 'no-cache',
    });

    if (getFullOrderQuery.error || !getFullOrderQuery.data?.order) {
      logger.warn(`Fetching full online order not found.`, {
        orderId,
        errorMsg: getFullOrderQuery.error ?? 'Unknown error',
      });
      return null;
    }

    return getFullOrderQuery.data.order;
  };

  useEffect(() => {
    // Skip adding pending orders if it is already in the acceptedOrderIdsRef.
    // Reason: Sometimes it takes a while for the backend to update the order status, and we are polling every 4 seconds,
    // so if there is a tiny mismatch between the backend and frontend, we might end up accepting the same order multiple times.
    const freshOrders = (ordersQuery.data?.orders || [])
      .map((o) => o.id)
      .filter((id) => !acceptedOrderIdsRef.current.has(id));

    setPendingOrderIds(freshOrders);

    // Play sound if there are new orders and the setting is enabled and we are not in the KDS (since it handle the sound itself)
    if (
      freshOrders.length > 0 &&
      storage.onlinePlayNewOrderSound &&
      !pageHref.includes('kitchen-display')
    ) {
      bridgeApi.playSound();
    }

    if (freshOrders.length > 0 && storage.autoShowIncomingAppPopup) {
      setShowingIncomingModal(true);
    } else if (freshOrders.length === 0) {
      setShowingIncomingModal(false);
    }

    // Remove orders from acceptedOrderIdsRef that are no longer pending and therefor fully handled (to not make the acceptedOrderIds grow indefinitely)
    const acceptedOrderIds = Array.from(acceptedOrderIdsRef.current);
    const orderIdsToClear = acceptedOrderIds.filter(
      (id) => !ordersQuery.data?.orders.some((o) => o.id === id),
    );
    orderIdsToClear.forEach((id) => {
      acceptedOrderIdsRef.current.delete(id);
      retryCountsRef.current.delete(id);
    });
  }, [ordersQuery.data?.orders]);

  // Function to retry accepting an order if it fails, it retries up to MAX_RETRIES times
  // and if it fails all the time, it will log a warning, the order will try again if a new order comes in.
  const addToRetry = (orderId: string) => {
    logger.info('Adding order to retry accept', {
      orderId,
      retryCount: retryCountsRef.current.get(orderId) || 0,
    });

    // Since it was optimistically added to the acceptedOrderIdsRef, we need to remove it
    // to allow retrying it again
    acceptedOrderIdsRef.current.delete(orderId); // allow retry on next poll

    retryCountsRef.current.set(orderId, (retryCountsRef.current.get(orderId) || 0) + 1);
    const retryCount = retryCountsRef.current.get(orderId) || 0;

    if (retryCount >= MAX_RETRIES) {
      logger.warn(`Max accept retries reached for online order`, {
        orderId,
      });
      return;
    }

    setTimeout(() => {
      logger.info('Retrying order accept', {
        orderId,
        retryCount,
      });

      setPendingOrderIds((prev) => {
        const newPendingOrders = [...prev];
        if (!newPendingOrders.includes(orderId)) {
          newPendingOrders.push(orderId);
        }
        return newPendingOrders;
      });
    }, 4000);
  };

  // --------------- ACCEPTING ONLINE ORDERS -------------------
  // Triggered from both auto accept and manual accept
  const acceptOrder = async (order: GetPendingOrders_venue_orders) => {
    logger.info(`Starting accept online order flow ${order.easyId}`, {
      orderId: order.id,
    });

    const refetchedOrder = await getOrderById(order.id);

    if (refetchedOrder?.status !== ORDER_STATUS.PENDING) {
      logger.info(`The order ${order.easyId} is already accepted, skipping`, {
        orderId: order.id,
        orderStatus: refetchedOrder?.status,
      });

      acceptedOrderIdsRef.current.add(order.id);
      setPendingOrderIds((prev) => prev.filter((id) => id !== order.id));
      return;
    }

    // --- Re-fetch fetch minimumPickupWaitTime in case it has changed ---
    let prepTimeMinutes = prepTime; // default fallback to the last known prep time
    try {
      const venuePrepTimeQuery = await client.query({
        query: GET_VENUE_PREP_TIME,
        variables: { venueId },
        fetchPolicy: 'no-cache',
      });

      if (
        venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime &&
        prepTime !== venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime
      ) {
        setPrepTime(venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime);
      }

      logger.info('Fetched minimumPickupWaitTime', {
        minimumPickupWaitTime: venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime,
      });

      prepTimeMinutes = venuePrepTimeQuery.data?.Venue?.minimumPickupWaitTime || prepTime; // fallback to the last known prep time
    } catch (error) {
      logger.warn('Failed to fetch minimumPickupWaitTime, using default', { errorData: error });
    }

    // -------------- PREPARE DATA ---------------
    // Will get converted to ISOString before passing to mutation
    const estimatedFinishTime = determineEstimatedTime(order, prepTimeMinutes);

    // If the order has a table, it is a table order and should be completed instantly
    // And if the setting is set to complete all orders instantly
    const newStatus =
      order.tableId || storageRef.current.completeAllAppOrdersInstantly
        ? ORDER_STATUS.COMPLETED
        : ORDER_STATUS.ACCEPTED;

    try {
      logger.info(`Attempting to accept online order ${order.easyId}`, {
        orderId: order.id,
        orderInfo: {
          orderEstimatedFinishTime: order.estimatedFinishTime,
          adjustedFinishTime: estimatedFinishTime,
          desiredTime: order.desiredTime,
          prepTime: prepTimeMinutes,
          newStatus,
        },
      });

      const result = await acceptOrderMutation({
        variables: {
          id: order.id,
          status: newStatus,
          estimatedFinishTime: estimatedFinishTime,
        },
      });

      // Check the response if OK
      if (result.data?.updateOrder.status === newStatus) {
        logger.info(`Successfully accepted online order ${order.easyId}`, {
          orderId: order.id,
          estimatedFinishTime,
          newStatus,
        });

        // Removes from pending
        setPendingOrderIds((prev) => prev.filter((id) => id !== order.id));

        // If there is only one pending order, we hide the modal since we are done
        if (pendingOrderIds.length === 1) {
          setShowingIncomingModal(false);
        }

        // TRIGGER POST ACTIONS
        postAcceptActions({
          ...order,
          estimatedFinishTime: estimatedFinishTime,
          status: newStatus,
        });
        return;
      }
    } catch (error) {
      console.error(error);
      logger.warn(`Failed to accept online order ${order.easyId}`, {
        orderId: order.id,
        order,
        errorMsg: error,
      });

      throw error;
    }
  };

  // --------------- REJECT ONLINE ORDERS -------------------
  const rejectOrder = async (orderId: string, reason: string) => {
    try {
      logger.info('Attempting to reject online order', {
        orderId,
        reason,
      });

      const res = await reject({
        variables: { id: orderId, rejectReason: reason },
      });

      if (res.data?.setOrderRejected.success) {
        logger.info(`Successfully rejected online order`, {
          orderId,
          reason,
        });

        // Removes from pending
        setPendingOrderIds((prev) => prev.filter((id) => id !== orderId));

        // If there is only one pending order, we hide the modal since we are done
        if (pendingOrderIds.length === 1) {
          setShowingIncomingModal(false);
        }
      }

      if (res.data?.setOrderRejected.error) {
        throw new Error(res.data?.setOrderRejected.error ?? 'Unknown error');
      }
    } catch (error) {
      logger.error(`Failed to reject order`, {
        orderId,
        reason,
        errorMsg: error,
      });

      throw error;
    }
  };

  // --------------- FETCHING FULL ORDERS (for manual accept modal) -------------------
  const fetchOrders = async () => {
    setOrderFetchingLoading(true);

    const fullOrders = await Promise.all(pendingOrderIds.map((orderId) => getOrderById(orderId)));
    setOrderFetchingLoading(false);
    setOrdersToHandle(fullOrders.filter((o) => o !== null));
  };

  useEffect(() => {
    if (showingIncomingModal) {
      fetchOrders();
    } else {
      setOrdersToHandle([]);
    }
  }, [showingIncomingModal, pendingOrderIds]);

  // --------------- AUTO ACCEPTING ONLINE ORDERS -------------------
  useEffect(() => {
    const isUsingBrowser = !window.ReactNativeWebView && !window.__DEV__;
    if (!storage.autoAcceptAppOrders || isUsingBrowser) return;

    if (pendingOrderIds.length > 0) {
      setIsAutoAccepting(true);
    } else {
      setTimeout(() => {
        setIsAutoAccepting(false);
      }, 3000);
    }

    pendingOrderIds.forEach(async (orderId) => {
      if (acceptedOrderIdsRef.current.has(orderId)) return;

      logger.info(`Starting auto-accepting online order`, {
        orderId,
      });

      // We remove the order from pending orders to avoid accepting it multiple times
      setPendingOrderIds((prev) => prev.filter((id) => id !== orderId));

      try {
        const order = await getOrderById(orderId);

        if (!order) {
          logger.warn(`Order not found in auto-accept`, {
            orderId,
          });
          return;
        }

        // We prematurely add the order to the acceptedOrderIdsRef
        acceptedOrderIdsRef.current.add(orderId);

        logger.info(`Trigger auto-accepting for online order ${order.easyId}`, {
          orderId,
        });

        acceptOrder(order).catch((err) => {
          logger.warn(`Failed to auto-accept online order ${order.easyId}, adding to retry`, {
            orderId,
            errorMsg: err,
          });

          // If the order fails to accept, we add it to the retry list
          addToRetry(orderId);
        });
      } catch (error) {
        console.error(error);
        logger.error(`Failed to auto-accept online order ${orderId}`, {
          errorMsg: error,
        });

        // If the order fails to accept, we add it to the retry list
        addToRetry(orderId);
      }
    });
  }, [pendingOrderIds, storage.autoAcceptAppOrders]);

  // --------------- POST ACCEPT ACTIONS -------------------
  // Called after each accepted order
  const postAcceptActions = (order: GetPendingOrders_venue_orders) => {
    if (hasSKVIntegration && hasSKVDevice) {
      orderUtility.syncWithSKV(order);
      orderUtility.addToZReport(order);
    } else if (hasSKVDevice) {
      orderUtility.addToZReport(order);
    }

    orderUtility.subtractOrderInventory(order);
    orderUtility.sendToReduxState(order);
  };

  const contextValue = {
    pendingOrdersIds: pendingOrderIds,
    ordersToHandle: ordersToHandle,
    orderFetchingLoading,
    isAutoAccepting,
    acceptOrder,
    rejectOrder,
    getOrderById,
    showingIncomingModal,
    showModal: () => setShowingIncomingModal(true),
    hideModal: () => setShowingIncomingModal(false),
    prepTime: {
      time: prepTime,
      loading: prepTimeQuery.loading,
      refetch: () => prepTimeQuery.refetch(),
    },
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}
